import { useState } from "react";
import { MenuItem } from "@kaltura/ds-react-components";
import { AddToWatchlistHelper, baseUrl, getKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

interface AddToWatchlistProps extends SharedMenuItemProps {
    entryId: string;
    categoryId?: string;
    inWatchlist?: boolean;
}

interface AddToWatchlistItemProps extends AddToWatchlistProps {
    label: string;
    disabled: boolean;
    onClick: () => void;
}

/**
 * Add to Watchlist menu item for media-page title area actions menu
 */
const AddToWatchlistItem = (props: AddToWatchlistItemProps) => {
    const { label, disabled, onClick, autoFocus } = props;

    return (
        <MenuItem
            disabled={disabled}
            onClick={onClick}
            autoFocus={autoFocus}
            className={"kms-ds-media-page-watchlist-button"}
        >
            {label}
        </MenuItem>
    );
};

/**
 * Add To Watch list item container - connect it to kms
 */
export const AddToWatchlistItemContainer = (props: AddToWatchlistProps) => {
    const { entryId, categoryId, onItemStateChange, onCloseMenu, inWatchlist: pInWatchList } = props;

    const [inWatchlist, setInWatchlist] = useState<boolean>(() => {
        // local storage value:
        const stored = AddToWatchlistHelper.getInWatchlist(entryId);

        // if prop has value, use that
        if (typeof pInWatchList === "boolean") {
            // update local storage if necessary
            if (pInWatchList && !stored) {
                AddToWatchlistHelper.addToWatchlist(entryId);
            }
            if (!pInWatchList && stored) {
                AddToWatchlistHelper.removeFromWatchlist(entryId);
            }

            return pInWatchList;
        }

        // no prop - use value from local storage
        return AddToWatchlistHelper.getInWatchlist(entryId);
    });

    const [disabled, setDisabled] = useState(false);
    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const label = inWatchlist ? translate("Remove from watch list") : translate("Add to watch list");

    const handleClick = async () => {
        const url =
            baseUrl
            + `/watchlater/media/`
            + (!inWatchlist ? `add/id/${entryId}` : `delete/id/${entryId}`)
            + (categoryId ? `/categoryid/${categoryId}` : "");

        try {
            setDisabled(true);

            sendButtonAnalytics(
                `Media Actions - ${inWatchlist ? "Remove from watch list" : "Add to watch list"}`,
                ButtonClickAnalyticsType.TOGGLE
            );

            const { result: success, message }: { result: boolean; message: string } = await getKmsData(url);

            if (success) {
                const newInWatchlist = !inWatchlist;

                // update local storage
                if (newInWatchlist) {
                    AddToWatchlistHelper.addToWatchlist(entryId);
                }
                else {
                    AddToWatchlistHelper.removeFromWatchlist(entryId);
                }

                // update state
                setInWatchlist(newInWatchlist);

                // from now on we do not care about the prop; localStorage is source of truth
                onItemStateChange?.("inWatchlist", undefined);
            }

            const severity = success ? "success" : "error";
            showToast({ severity, message });
        }
        catch (e: unknown) {
            showToast({ severity: "error", message: translate("Failed to update watch list.") });
            console.error("Failed to add/delete add to watch later", e, { url });
        }

        setDisabled(false);
        onCloseMenu?.();
    };

    return <AddToWatchlistItem {...props} label={label} disabled={disabled} onClick={handleClick} />;
};
